import DataApi from '../api/DataApi';

const convertAggregationValue = (dbValue) => {
  const mapping = {
    average: 'avg',
    avg: 'avg',
    sum: 'sum',
    min: 'min',
    max: 'max',
    count: 'count',
    median: 'median'
  };
  return mapping[dbValue] || dbValue || 'sum';
};

const formatValue = (fieldData, dataType) => {
  if (typeof fieldData === 'object' && fieldData !== null) {
    if (fieldData.isReference && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    if ((fieldData.isPicklist || fieldData.isMultiPicklist) && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    const value = fieldData.value;
    if (value === null || value === undefined || value === '') return 'N/A';

    switch (dataType) {
      case 'currency': {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        return `₹${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      case 'number':
      case 'decimal': {
        const numVal = parseFloat(value);
        if (isNaN(numVal)) return 'N/A';
        return numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      case 'percent': {
        const percentVal = parseFloat(value);
        if (isNaN(percentVal)) return 'N/A';
        return `${percentVal.toFixed(2)}%`;
      }
      default:
        return value;
    }
  }

  if (fieldData === null || fieldData === undefined || fieldData === '') {
    return 'N/A';
  }

  switch (dataType) {
    case 'currency': {
      const numValue = parseFloat(fieldData);
      if (isNaN(numValue)) return 'N/A';
      return `₹${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    case 'number':
    case 'decimal': {
      const numVal = parseFloat(fieldData);
      if (isNaN(numVal)) return 'N/A';
      return numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    case 'percent': {
      const percentVal = parseFloat(fieldData);
      if (isNaN(percentVal)) return 'N/A';
      return `${percentVal.toFixed(2)}%`;
    }
    default:
      return fieldData;
  }
};

const calculateAggregation = (data, field, aggregation) => {
  if (!data || data.length === 0) return 'N/A';

  const tableName = field.object_name || 'main';
  const aliasedFieldName = `${tableName}_${field.field_name}`;

  const values = data
    .map(row => {
      const fieldData = row[aliasedFieldName] || row[field.field_name];

      if (typeof fieldData === 'object' && fieldData !== null) {
        const value = fieldData.value;
        return value !== null && value !== undefined && value !== '' ? parseFloat(value) : null;
      }

      return fieldData !== null && fieldData !== undefined && fieldData !== ''
        ? parseFloat(fieldData)
        : null;
    })
    .filter(val => val !== null && !isNaN(val));

  if (values.length === 0) return 'N/A';

  let result;
  switch (aggregation) {
    case 'sum':
      result = values.reduce((sum, val) => sum + val, 0);
      break;
    case 'average':
    case 'avg':
      result = values.reduce((sum, val) => sum + val, 0) / values.length;
      break;
    case 'max':
      result = Math.max(...values);
      break;
    case 'min':
      result = Math.min(...values);
      break;
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      result = sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
      break;
    }
    case 'count':
      return values.length.toString();
    default:
      return 'N/A';
  }

  return formatValue(result, field.data_type);
};

export const transformDatasetToPreviewData = (dataset) => {
  if (!dataset || !dataset._metadata) {
    return [];
  }

  const metadata = dataset._metadata;
  const groupFields = metadata.group_fields || {};
  const summarizedFields = metadata.summarized_fields || {};
  const previewData = [];

  const primaryGroupFieldName = Object.keys(groupFields)[0];
  if (!primaryGroupFieldName) {
    return [];
  }

  const primaryGroupData = dataset[primaryGroupFieldName];
  if (!primaryGroupData) {
    return [];
  }

  Object.keys(primaryGroupData).forEach(groupValue => {
    const groupData = primaryGroupData[groupValue];
    const row = {
      [primaryGroupFieldName]: groupValue
    };

    Object.keys(summarizedFields).forEach(fieldName => {
      if (fieldName === 'record_count') {
        const recordCountValue = groupData?.record_count?.value ?? dataset?.record_count?.[groupValue];
        if (recordCountValue !== undefined) {
          row[fieldName] = recordCountValue;
        }
        return;
      }

      const fieldData = groupData[fieldName];
      if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
        row[fieldName] = fieldData.value;
      } else if (fieldData !== undefined) {
        row[fieldName] = fieldData;
      }
    });

    previewData.push(row);
  });

  const groupFieldNames = Object.keys(groupFields);
  if (groupFieldNames.length > 1) {
    const additionalRows = [];

    groupFieldNames.slice(1).forEach(groupFieldName => {
      const groupData = dataset[groupFieldName];
      if (groupData) {
        Object.keys(groupData).forEach(groupValue => {
          const fieldData = groupData[groupValue];
          const row = {
            [groupFieldName]: groupValue
          };

          Object.keys(summarizedFields).forEach(fieldName => {
            const summarizedFieldData = fieldData[fieldName];
            if (summarizedFieldData && typeof summarizedFieldData === 'object' && summarizedFieldData.value !== undefined) {
              row[fieldName] = summarizedFieldData.value;
            } else if (summarizedFieldData !== undefined) {
              row[fieldName] = summarizedFieldData;
            }
          });

          additionalRows.push(row);
        });
      }
    });

    previewData.push(...additionalRows);
  }

  return previewData;
};

function normalizePicklistValuesArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Copy picklist_values (incl. path_color) from report field definitions into dataset group_fields
 * so charts can color categories by picklist. Handles array or JSON string; also picklistValues (camelCase).
 */
export function enrichDatasetGroupFieldsWithPicklist(dataset, reportFields) {
  if (!dataset?._metadata?.group_fields || !Array.isArray(reportFields) || reportFields.length === 0) {
    return;
  }
  for (const name of Object.keys(dataset._metadata.group_fields)) {
    const meta = dataset._metadata.group_fields[name];
    const match =
      reportFields.find(
        f =>
          f.field_name === name && (f.object_name || 'main') === (meta.object_name || 'main')
      ) || reportFields.find(f => f.field_name === name);
    if (!match) continue;
    const pl =
      normalizePicklistValuesArray(match.picklist_values_data) ||
      normalizePicklistValuesArray(match.picklist_values) ||
      normalizePicklistValuesArray(match.picklistValues);
    if (pl && pl.length > 0) {
      dataset._metadata.group_fields[name] = {
        ...meta,
        picklist_values: pl,
        data_type: meta.data_type || match.data_type
      };
    }
  }
}

export const extractReportFieldMetadata = (fields = []) => {
  const selectedFields = [];
  const selectedGroups = [];
  const summarizedFields = {};

  fields.forEach(field => {
    const id =
      field.field_config_id ||
      field.id ||
      `${field.object_name || 'main'}.${field.field_name}`;

    const normalizedField = {
      id,
      field_name: field.field_name,
      field_label: field.field_label || field.label || field.field_name,
      data_type: field.data_type,
      object_name: field.object_name,
      field_type: field.field_type,
      is_grouped: field.is_grouped,
      sort_order: field.sort_order,
      summaryType: field.is_aggregated && field.aggregate_function
        ? convertAggregationValue(field.aggregate_function)
        : 'none'
    };

    if (field.is_grouped) {
      selectedGroups.push(normalizedField);
    } else {
      selectedFields.push(normalizedField);
    }

    if (field.is_aggregated && field.aggregate_function) {
      summarizedFields[id] = {
        aggregation: convertAggregationValue(field.aggregate_function)
      };
    }
  });

  return {
    selectedFields,
    selectedGroups,
    summarizedFields
  };
};

const buildSummarizedDataset = (rawData, grouping, fieldMetadata) => {
  if (!grouping || !grouping.hasGrouping || !rawData || rawData.length === 0) {
    return {};
  }

  const groupedFields = grouping.groupedFields || [];
  const allFields = fieldMetadata.selectedFields || [];
  const summarizedFieldMap = fieldMetadata.summarizedFields || {};

  const nonGroupedFields = allFields.filter(field =>
    !groupedFields.some(groupField =>
      groupField.field_name === field.field_name &&
      (groupField.object_name || 'main') === (field.object_name || 'main')
    )
  );

  const summarizedData = {};
  summarizedData._metadata = {
    group_fields: {},
    summarized_fields: {},
    report_info: {
      total_records: rawData.length,
      group_count: groupedFields.length,
      summarized_field_count: Object.keys(summarizedFieldMap).length,
      extraction_timestamp: new Date().toISOString(),
      data_structure_version: '2.0'
    }
  };

  groupedFields.forEach(field => {
    summarizedData._metadata.group_fields[field.field_name] = {
      field_id: field.field_config_id || field.id || `${field.object_name || 'main'}.${field.field_name}`,
      field_name: field.field_name,
      field_label: field.field_label || field.field_name,
      data_type: field.data_type,
      object_name: field.object_name,
      field_type: field.field_type
    };
  });

  nonGroupedFields.forEach(field => {
    const summarizedField = summarizedFieldMap[field.id];
    if (summarizedField) {
      summarizedData._metadata.summarized_fields[field.field_name] = {
        field_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        object_name: field.object_name,
        aggregation: summarizedField.aggregation
      };
    }
  });

  if (groupedFields.length > 0) {
    summarizedData._metadata.summarized_fields.record_count = {
      field_id: 'record_count',
      field_name: 'record_count',
      field_label: 'Record Count',
      data_type: 'number',
      object_name: 'virtual',
      aggregation: 'count',
      is_virtual: true
    };
  }

  if (groupedFields.length > 0) {
    groupedFields.forEach((primaryGroupField, idx) => {
      const groupFieldName = primaryGroupField.field_name;
      const groupValues = new Set();

      rawData.forEach(row => {
        const tableName = primaryGroupField.object_name || 'main';
        const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
        const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

        let groupValue;
        if (typeof fieldData === 'object' && fieldData !== null) {
          groupValue = fieldData.value || fieldData.displayValue;
        } else {
          groupValue = fieldData;
        }

        if (groupValue === null || groupValue === undefined || groupValue === '') {
          groupValue = 'N/A';
        }

        groupValues.add(groupValue);
      });

      if (idx === 0) {
        nonGroupedFields.forEach(field => {
          const summarizedField = summarizedFieldMap[field.id];
          if (summarizedField) {
            if (!summarizedData[field.field_name]) {
              summarizedData[field.field_name] = {};
            }
          }
        });
        if (!summarizedData.record_count) {
          summarizedData.record_count = {};
        }
      }

      groupValues.forEach(groupValue => {
        const groupData = rawData.filter(row => {
          const tableName = primaryGroupField.object_name || 'main';
          const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
          const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

          let rowGroupValue;
          if (typeof fieldData === 'object' && fieldData !== null) {
            rowGroupValue = fieldData.value || fieldData.displayValue;
          } else {
            rowGroupValue = fieldData;
          }

          if (rowGroupValue === null || rowGroupValue === undefined || rowGroupValue === '') {
            rowGroupValue = 'N/A';
          }

          return rowGroupValue === groupValue;
        });

        nonGroupedFields.forEach(field => {
          const summarizedField = summarizedFieldMap[field.id];
          if (summarizedField) {
            const aggregatedValue = calculateAggregation(groupData, field, summarizedField.aggregation);
            let chartValue = aggregatedValue;

            if (field.data_type === 'currency' && typeof aggregatedValue === 'string') {
              const numericValue = parseFloat(aggregatedValue.replace(/[₹,]/g, ''));
              chartValue = isNaN(numericValue) ? 0 : numericValue;
            } else if (field.data_type === 'number' || field.data_type === 'decimal') {
              const numericValue = parseFloat(aggregatedValue);
              chartValue = isNaN(numericValue) ? 0 : numericValue;
            }

            summarizedData[field.field_name][groupValue] = chartValue;
          }
        });

        const recordCount = groupData.length;
        summarizedData.record_count[groupValue] = recordCount;
      });

      summarizedData[groupFieldName] = {};
      groupValues.forEach(groupValue => {
        summarizedData[groupFieldName][groupValue] = {};
        nonGroupedFields.forEach(field => {
          const summarizedField = summarizedFieldMap[field.id];
          if (summarizedField && summarizedData[field.field_name]?.[groupValue] !== undefined) {
            summarizedData[groupFieldName][groupValue][field.field_name] = {
              value: summarizedData[field.field_name][groupValue]
            };
          }
        });

        if (summarizedData.record_count[groupValue] !== undefined) {
          summarizedData[groupFieldName][groupValue].record_count = {
            value: summarizedData.record_count[groupValue]
          };
        }
      });
    });
  }

  return summarizedData;
};

const getRowFieldData = (row, field) => {
  const tableName = field.object_name || 'main';
  const aliasedFieldName = `${tableName}_${field.field_name}`;
  return row?.[aliasedFieldName] ?? row?.[field.field_name];
};

const extractNumericValue = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') {
    const v = value.value ?? value.displayValue;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const extractGroupValue = (row, field) => {
  const fieldData = getRowFieldData(row, field);
  let groupValue;
  if (typeof fieldData === 'object' && fieldData !== null) {
    groupValue = fieldData.value ?? fieldData.displayValue;
  } else {
    groupValue = fieldData;
  }
  if (groupValue === null || groupValue === undefined || groupValue === '') {
    return 'N/A';
  }
  return groupValue;
};

const buildSummarizedDatasetFromGroupedRows = (rawData, grouping, fieldMetadata) => {
  const groupedFields = grouping?.groupedFields || [];
  if (!grouping?.hasGrouping || groupedFields.length === 0 || !Array.isArray(rawData) || rawData.length === 0) {
    return {};
  }

  const allFields = fieldMetadata.selectedFields || [];
  const summarizedFieldMap = fieldMetadata.summarizedFields || {};
  const nonGroupedFields = allFields.filter(field =>
    !groupedFields.some(groupField =>
      groupField.field_name === field.field_name &&
      (groupField.object_name || 'main') === (field.object_name || 'main')
    )
  );

  const summarizedData = {
    _metadata: {
      group_fields: {},
      summarized_fields: {},
      report_info: {
        total_records: rawData.length,
        group_count: groupedFields.length,
        summarized_field_count: Object.keys(summarizedFieldMap).length,
        extraction_timestamp: new Date().toISOString(),
        data_structure_version: '2.0'
      }
    }
  };

  groupedFields.forEach(field => {
    summarizedData._metadata.group_fields[field.field_name] = {
      field_id: field.field_config_id || field.id || `${field.object_name || 'main'}.${field.field_name}`,
      field_name: field.field_name,
      field_label: field.field_label || field.field_name,
      data_type: field.data_type,
      object_name: field.object_name,
      field_type: field.field_type
    };
  });

  nonGroupedFields.forEach(field => {
    const summarizedField = summarizedFieldMap[field.id];
    if (summarizedField) {
      summarizedData._metadata.summarized_fields[field.field_name] = {
        field_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        object_name: field.object_name,
        aggregation: summarizedField.aggregation
      };
      summarizedData[field.field_name] = {};
    }
  });

  summarizedData._metadata.summarized_fields.record_count = {
    field_id: 'record_count',
    field_name: 'record_count',
    field_label: 'Record Count',
    data_type: 'number',
    object_name: 'virtual',
    aggregation: 'count',
    is_virtual: true
  };
  summarizedData.record_count = {};

  groupedFields.forEach(primaryGroupField => {
    const groupFieldName = primaryGroupField.field_name;
    summarizedData[groupFieldName] = {};

    rawData.forEach(row => {
      const groupValue = extractGroupValue(row, primaryGroupField);
      if (!summarizedData[groupFieldName][groupValue]) {
        summarizedData[groupFieldName][groupValue] = {};
      }

      nonGroupedFields.forEach(field => {
        const summarizedField = summarizedFieldMap[field.id];
        if (!summarizedField) return;
        const n = extractNumericValue(getRowFieldData(row, field));
        if (n == null) return;
        summarizedData[field.field_name][groupValue] =
          (summarizedData[field.field_name][groupValue] || 0) + n;
        summarizedData[groupFieldName][groupValue][field.field_name] = {
          value: summarizedData[field.field_name][groupValue]
        };
      });

      const explicitRecordCount = extractNumericValue(row?.record_count ?? row?.main_record_count);
      const countValue = explicitRecordCount != null ? explicitRecordCount : 1;
      summarizedData.record_count[groupValue] = (summarizedData.record_count[groupValue] || 0) + countValue;
      summarizedData[groupFieldName][groupValue].record_count = {
        value: summarizedData.record_count[groupValue]
      };
    });
  });

  return summarizedData;
};

export const fetchReportChartData = async ({
  reportId,
  chartId,
  limit = 10000,
  filters = {},
  existingChartConfig = null,
  runningUserId = null,
  dashboardId = null,
  viewAsUserId = null,
}) => {
  if (!reportId) {
    throw new Error('reportId is required to fetch report chart data');
  }

  const reportBuilderApi = new DataApi('reportbuilder');
  const reportResponse = await reportBuilderApi.get(`/${reportId}`);

  if (!reportResponse?.data?.success) {
    throw new Error(reportResponse?.data?.message || 'Failed to load report configuration');
  }

  const report = reportResponse.data.data || {};
  const fieldMetadata = extractReportFieldMetadata(report.fields || []);

  let chartConfig = existingChartConfig || null;
  if (report.charts && report.charts.length > 0) {
    if (chartId) {
      chartConfig = report.charts.find(chart => chart.id === chartId) || chartConfig;
    }
    if (!chartConfig) {
      chartConfig = report.charts[0];
    }
  }

  const dataApi = new DataApi('object');
  const viewPayload = {
    reportId,
    limit,
    filters,
  };
  if (runningUserId) {
    viewPayload.runningUserId = runningUserId;
  }
  if (dashboardId) {
    viewPayload.dashboardId = dashboardId;
  }
  if (viewAsUserId) {
    viewPayload.viewAsUserId = viewAsUserId;
  }
  const dataResponse = await dataApi.post('/view/report', viewPayload);

  if (!dataResponse?.data?.success) {
    throw new Error(dataResponse?.data?.message || 'Failed to load report data');
  }

  const rawData = dataResponse.data.data || [];
  const groupingInfo = dataResponse.data.grouping || null;

  const dataset = groupingInfo?.hasGrouping
    ? buildSummarizedDatasetFromGroupedRows(rawData, groupingInfo, fieldMetadata)
    : buildSummarizedDataset(rawData, groupingInfo, fieldMetadata);
  enrichDatasetGroupFieldsWithPicklist(dataset, report.fields || []);
  const previewData = Object.keys(dataset || {}).length > 0
    ? transformDatasetToPreviewData(dataset)
    : rawData;

  return {
    report,
    chartConfig,
    rawData,
    dataset,
    previewData,
    groupingInfo,
    selectedFields: fieldMetadata.selectedFields,
    summarizedFields: fieldMetadata.summarizedFields,
    selectedGroups: fieldMetadata.selectedGroups
  };
};

export default fetchReportChartData;
